// frontend/src/charts/csv.js
// A saved chart's data as CSV (Phase 3, download fix after 10b). Pure and
// unit-tested; the page turns the text into a file.
//
// Columns follow the chart's saved order (tableOrder.js); numbers are written
// as plain numbers, not in the chart's display format, so a spreadsheet can
// add them up. For a matrix the "_totals" marker is not a column: a field
// rolled up into a subtotal or the grand total is written as "Total".
//
// Spreadsheet safety: a text value starting with = + - @ (or a tab or
// carriage return) is prefixed with an apostrophe, so Excel and Sheets show
// it as text instead of running it as a formula (CSV injection).
import { columnLabel } from './format';
import { orderColumns } from './tableOrder';

const FORMULA_START = /^[=+\-@\t\r]/;

/** One value as a CSV field. */
export function csvField(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  let s = String(v);
  if (FORMULA_START.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** columns + rows (the chart data endpoint's shape) -> CSV text, header first, CRLF line ends. */
export function toCsv(columns, rows, presentationColumns = null) {
  const all = columns || [];
  const at = new Map(all.map((c, i) => [c.alias, i]));
  const marker = all.find((c) => c.kind === 'totals');
  const dims = all.filter((c) => c.kind === 'dimension');
  const shown = orderColumns(all.filter((c) => c.kind !== 'totals'), presentationColumns);
  const lines = [shown.map((c) => csvField(columnLabel(c))).join(',')];
  for (const row of rows || []) {
    const mask = marker ? Number(row[at.get(marker.alias)]) || 0 : 0;
    lines.push(shown.map((c) => {
      if (mask && c.kind === 'dimension') {
        const bit = 1 << (dims.length - 1 - dims.indexOf(c));
        if (mask & bit) return csvField('Total');
      }
      return csvField(row[at.get(c.alias)]);
    }).join(','));
  }
  return `${lines.join('\r\n')}\r\n`;
}

/** A file name from the chart's title: letters, digits, dashes; never empty. */
export function csvFileName(title, id) {
  const base = String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  return `${base || `connex-chart-${id}`}.csv`;
}